import { createAsyncThunk } from '@reduxjs/toolkit';
import { ApiManager } from '../../services/ApiManager';
import { setSettingsLoading, setSettingsData, setSettingsError } from '../slices/settingsSlice';
import { RootState } from '../index';

// Async thunk for fetching settings
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (
    params: {
      language: string;
      experiments?: string;
      sessionId?: string;
    },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;

      // Check if we already have settings and they're not too old (optional caching)
      const { data: currentSettings, lastFetched, loading } = state.settings;

      // Don't fetch if already loading
      if (loading) {
        return rejectWithValue('Already loading settings');
      }

      // Optional: Skip if we have recent settings (less than 5 minutes old)
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
      if (currentSettings && lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
        return currentSettings;
      }

      dispatch(setSettingsLoading(true));

      const response = await ApiManager.customer.getScrollCollectSettings(
        params.language,
        params.experiments || '',
        params.sessionId
      );

      dispatch(setSettingsData(response.data));
      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch settings';
      dispatch(setSettingsError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

// Helper function to get settings from state or fetch if needed
export const ensureSettings = createAsyncThunk(
  'settings/ensureSettings',
  async (
    params: {
      language: string;
      experiments?: string;
      sessionId?: string;
    },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;
    const { data: currentSettings } = state.settings;

    if (!currentSettings) {
      return dispatch(fetchSettings(params)).unwrap();
    }

    return currentSettings;
  }
);
